import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Footer from "./Footer";
import "../css/generalSettingsPage.scss";
import "../css/templatesSettings.scss";
import { Switch } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { WORKFLOWS, getTemplateKey } from "../helperFunctions/workflows";
import { formatSubject } from "../helperFunctions/pdfFunctions";
import { buildTemplateVars, getTemplateMessage, renderTemplate } from "../helperFunctions/templateEngine";
import { styleMessageText } from "../helperFunctions/emailPreviewHtml";
import { TemplatesContext } from "../providers/TemplatesProvider";
import { SettingsContext } from "../providers/SettingsProvider";
import RichTemplateEditor, { TOOLBAR_BUTTONS } from "./RichTemplateEditor";

const CATEGORY_TABS = [
  { key: "all", label: "All" },
  { key: "quote", label: "Quotes" },
  { key: "invoice", label: "Invoices" },
  { key: "paid", label: "Paid" },
];

// Sample data used to render every template with realistic placeholder values - this page
// is a preview/editor, nothing here is ever sent to a customer.
const SAMPLE = {
  name: "John",
  refNumber: "1234",
  amount: "150.00",
  addressLine: "12 Example Street, Glasgow, G1 1AA",
  jobDetails: "Example job details describing the work carried out.",
  showReview: false,
};

function buildTemplateEntries(vatMode, templates) {
  return WORKFLOWS.flatMap((workflow) => {
    let subjectBase = {
      pdfType: workflow.pdfType,
      subjectPrefix: workflow.subjectPrefix,
      timesheet: { refNumber: SAMPLE.refNumber, address: "Example Street", authorisedBy: "John Smith" },
    };

    function makeEntry(emailType, label, isPaid) {
      let templateKey = getTemplateKey(workflow.key, emailType, vatMode);
      return {
        templateKey,
        label: vatMode ? `${label} (VAT)` : label,
        subject: formatSubject({ ...subjectBase, isPaid }),
        rawMessage: getTemplateMessage(templateKey, templates),
        vars: buildTemplateVars(SAMPLE),
        category: isPaid ? "paid" : workflow.pdfType === "Quote" ? "quote" : "invoice",
      };
    }

    let entries = [makeEntry(workflow.pdfType, `${workflow.pdfType} — ${workflow.label}`, false)];

    if (workflow.pdfType === "Invoice") {
      entries.push(makeEntry("Paid", `${workflow.pdfType} — ${workflow.label} (Paid)`, true));
    }

    return entries;
  });
}

function TemplateCard({ entry, onSave }) {
  // Each card controls its own view mode independently - previously a single page-wide
  // toggle switched every card at once.
  const [rendered, setRendered] = useState(true);

  // The editor seeds itself with shorthand (**bold** etc.) already converted to real HTML,
  // so a legacy template opens up nicely styled instead of showing raw markup characters.
  // The dirty-check baseline (savedValue) uses the same conversion so an untouched template
  // correctly reads as "not dirty" rather than permanently differing from its own stored form.
  let initialNormalized = useMemo(() => styleMessageText(entry.rawMessage), [entry.rawMessage]);

  const [draft, setDraft] = useState(initialNormalized);
  const [savedValue, setSavedValue] = useState(initialNormalized);
  // Bumped only when the effect below re-seeds from a genuinely new source value, to force
  // RichTemplateEditor to remount and pick up the correction - never on every keystroke.
  const [seedVersion, setSeedVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isDirty = draft !== savedValue;
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const editorRef = useRef(null);

  // On first mount, `templates` (the Firestore override map, via TemplatesContext) is still
  // {} - the onSnapshot listener hasn't delivered its first batch yet - so entry.rawMessage
  // briefly falls back to the built-in default even for a template that HAS been edited and
  // saved before. This re-seeds once the real data streams in a moment later, correcting
  // that - but only when the user hasn't started editing, so a later external update (e.g.
  // another tab saving a change) can never clobber in-progress local edits.
  useEffect(() => {
    if (!isDirtyRef.current) {
      setDraft(initialNormalized);
      setSavedValue(initialNormalized);
      setSeedVersion((v) => v + 1);
    }
  }, [initialNormalized]);

  function handleEditorChange(html) {
    setDraft(html);
    setError(null);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await onSave(entry.templateKey, draft);
      setSavedValue(draft);
    } catch (err) {
      setError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="templateCard">
      <div className="templateCardHeader">
        <div className="templateCardHeaderLabel">
          <h4>{entry.label}</h4>
        </div>
        <div className="templateModeToggle">
          <button
            type="button"
            className={"templateModeIconBtn" + (!rendered ? " templateModeIconBtnActive" : "")}
            title="Edit"
            onClick={() => setRendered(false)}
          >
            <EditIcon fontSize="small" />
          </button>
          <span className="templateModeSeparator">/</span>
          <button
            type="button"
            className={"templateModeIconBtn" + (rendered ? " templateModeIconBtnActive" : "")}
            title="Preview as email"
            onClick={() => setRendered(true)}
          >
            <MailOutlineIcon fontSize="small" />
          </button>
        </div>
      </div>
      <div className="templateField">
        <span className="templateFieldLabel">Subject</span>
        <p className="templateSubject">{entry.subject}</p>
      </div>
      <div className="templateField">
        <span className="templateFieldLabel">Message</span>
        {rendered ? (
          <div
            className="templateMessageRendered"
            dangerouslySetInnerHTML={{ __html: styleMessageText(renderTemplate(draft, entry.vars)) }}
          />
        ) : (
          <RichTemplateEditor
            key={seedVersion}
            ref={editorRef}
            initialHtml={draft}
            onChange={handleEditorChange}
          />
        )}
      </div>
      <div className="templateCardFooter">
        <div className="templateCardFooterStatus">
          <button className="templateSaveBtn" disabled={!isDirty || saving} onClick={handleSave}>
            {saving ? "Saving…" : isDirty ? "Save" : "Saved"}
          </button>
          {error ? (
            <span className="templateErrorNote">Couldn&apos;t save: {error}</span>
          ) : (
            isDirty && !saving && <span className="unsavedNote">Unsaved changes</span>
          )}
        </div>
        {!rendered && (
          <div className="richEditorToolbar">
            {TOOLBAR_BUTTONS.map((btn) => (
              <button
                key={btn.marker}
                type="button"
                className="richEditorToolbarBtn"
                title={btn.title}
                style={btn.style}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editorRef.current?.wrapSelection(btn.marker)}
              >
                {btn.label}
              </button>
            ))}
            <button
              type="button"
              className="richEditorToolbarBtn richEditorClearBtn"
              title="Clear formatting from the selected text"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editorRef.current?.clearFormatting()}
            >
              Clear
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const TemplatesSettings = () => {
  const { vat: vatSetting } = useContext(SettingsContext);
  const [vatMode, setVatMode] = useState(vatSetting);
  // SettingsContext starts out at whatever default the provider's own useState began with,
  // then corrects itself a moment later once its Firestore onSnapshot actually resolves -
  // so seeding vatMode from vatSetting only on mount (the naive useState(vatSetting) above)
  // would often catch that stale default instead of the real setting. Re-seed once the real
  // value streams in, but only until the user actually touches the toggle themselves.
  const [vatModeTouched, setVatModeTouched] = useState(false);
  useEffect(() => {
    if (!vatModeTouched) setVatMode(vatSetting);
  }, [vatSetting, vatModeTouched]);

  const [activeCategory, setActiveCategory] = useState("all");
  const { templates, saveTemplateMessage } = useContext(TemplatesContext);

  const entries = useMemo(() => buildTemplateEntries(vatMode, templates), [vatMode, templates]);
  const visibleEntries = useMemo(
    () => (activeCategory === "all" ? entries : entries.filter((e) => e.category === activeCategory)),
    [entries, activeCategory]
  );

  return (
    <div className="generalSettingsPage templatesSettingsPage">
      <Footer title={"Templates"} back={true} />
      <div className="settingsOptionsContainer">
        <div className="templateCategoryTabs">
          {CATEGORY_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={
                "templateCategoryTab" + (activeCategory === tab.key ? " templateCategoryTabActive" : "")
              }
              onClick={() => setActiveCategory(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="formLine">
          <label htmlFor="vatModePreview">Preview with VAT mode on</label>
          <Switch
            id="vatModePreview"
            checked={vatMode}
            onChange={() => {
              setVatModeTouched(true);
              setVatMode((v) => !v);
            }}
          />
        </div>

        <details className="templateKey">
          <summary>Available variables &amp; formatting</summary>
          <p className="templateKeyPasteNote">
            You can also copy formatted text from Gmail (or anywhere else) and paste it
            straight into a template below - bold, italic, underline and links are kept,
            other formatting (colours, fonts, spacing) is dropped so it doesn&apos;t carry
            over into the real email. Use the <b>Clear</b> button to strip formatting from a
            selection entirely. Just add the <code>{"{{variables}}"}</code> yourself
            afterwards, wherever they&apos;re needed.
          </p>
          <div className="templateKeyBody">
            <div className="templateKeySection">
              <h5>Variables</h5>
              <ul>
                <li>
                  <code>{"{{name}}"}</code> — customer&apos;s first name
                </li>
                <li>
                  <code>{"{{refNumber}}"}</code> — quote/invoice reference number
                </li>
                <li>
                  <code>{"{{addressLine}}"}</code> — job address (Invoice templates)
                </li>
                <li>
                  <code>{"{{jobDetails}}"}</code> — job description (Quote general only)
                </li>
                <li>
                  <code>{"{{amount}}"}</code> — amount paid (Paid templates only)
                </li>
                <li>
                  <code>{"{{reviewText}}"}</code> — Google review request, only appears when
                  the &quot;Review&quot; checkbox is ticked
                </li>
                <li>
                  <code>{"{{signOff}}"}</code> — standard sign-off + wiring regulations line
                </li>
              </ul>
            </div>
            <div className="templateKeySection">
              <h5>Formatting</h5>
              <ul>
                <li>
                  <code>**text**</code> → <b>bold</b>
                </li>
                <li>
                  <code>*text*</code> → <i>italic</i>
                </li>
                <li>
                  <code>***text***</code> →{" "}
                  <b>
                    <i>bold italic</i>
                  </b>
                </li>
                <li>
                  <code>__text__</code> → <u>underline</u>
                </li>
                <li>
                  <code>^^text^^</code> → <em style={{ fontSize: 11 }}>small text</em>
                </li>
              </ul>
            </div>
          </div>
        </details>

        <div className="templateList">
          {visibleEntries.map((entry) => (
            <TemplateCard key={entry.templateKey} entry={entry} onSave={saveTemplateMessage} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TemplatesSettings;
